"use client";

import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import ContactForm from "../Contact/ContactForm";
import axios from "axios";
import Card from "../Home/Card";
import "./CardDetail.css";

export default function CardDetail() {
  const { recordNumber } = useParams();
  const [car, setCar] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [relatedCars, setRelatedCars] = useState([]);
  const contactFormRef = useRef(null);

  useEffect(() => {
    if (!recordNumber) return;

    setLoading(true);
    axios
      .get(`https://automotive-backend-production.up.railway.app/inventory/record/${recordNumber}`)
      .then((res) => {
        setCar(res.data);
        setCurrentImageIndex(0);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch car details", err);
        setLoading(false);
      });
  }, [recordNumber]);

  useEffect(() => {
    if (!car?.Category) return;

    axios
      .get(
        `https://automotive-backend-production.up.railway.app/inventory/search?Category=${car.Category}&limit=4`
      )
      .then((res) => {
        const filtered = res.data.cars.filter((c) => c._id !== car._id);
        setRelatedCars(filtered);
      })
      .catch((err) => console.error("Failed to fetch related cars", err));
  }, [car]);

  // Create gallery images with thumbnail first
  const galleryImages = car
    ? car.Thumbnail
      ? [car.Thumbnail, ...(car.ImageURL?.filter((img) => img !== car.Thumbnail) || [])]
      : car.ImageURL || []
    : [];

  const nextImage = () => {
    if (galleryImages.length > 0) {
      setCurrentImageIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
    }
  };

  const prevImage = () => {
    if (galleryImages.length > 0) {
      setCurrentImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
    }
  };

  const goToImage = (index) => setCurrentImageIndex(index);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading car details...</p>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="not-found-container">
        <h3>Car not found</h3>
        <p>The requested vehicle could not be found.</p>
      </div>
    );
  }

  const currentImage = galleryImages?.[currentImageIndex]
    ? `https://automotive-backend-production.up.railway.app/${galleryImages[currentImageIndex]}`
    : "/placeholder.svg?height=400&width=600";

  const specifications = [
    { label: "Make", value: car.Make, icon: "🏭" },
    { label: "Model", value: car.Model, icon: "🚗" },
    { label: "Color", value: car.Color, icon: "🎨" },
    { label: "Drive Type", value: car.DriveType, icon: "⚙️" },
    { label: "Condition", value: car.Condition, icon: "✨" },
    { label: "Engine Size", value: car.EngineSize, icon: "🔧" },
    { label: "Doors", value: car.Doors, icon: "🚪" },
    { label: "Seats", value: car.Seats, icon: "💺" },
    { label: "Category", value: car.Category, icon: "📋" },
    { label: "Steering", value: car.Steering, icon: "🎯" },
  ];

  return (
    <div className="card-detail-container">
      <div className="container card-detail-content">
        <div className="row">
          {/* Left Side - Image Gallery */}
          <div className="col-lg-8 mb-4">
            <div className="main-image-container">
              <div className="main-image-wrapper">
                <img
                  src={currentImage}
                  className="main-image"
                  alt={car.Name}
                  onError={(e) => {
                    e.target.src = "/placeholder.svg?height=500&width=800";
                  }}
                />
                {galleryImages.length > 1 && (
                  <>
                    <button onClick={prevImage} className="nav-arrow prev">
                      <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <polyline points="15,18 9,12 15,6"></polyline>
                      </svg>
                    </button>
                    <button onClick={nextImage} className="nav-arrow next">
                      <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <polyline points="9,18 15,12 9,6"></polyline>
                      </svg>
                    </button>
                    <div className="image-counter">
                      {currentImageIndex + 1} / {galleryImages.length}
                    </div>
                  </>
                )}
              </div>
            </div>

            {galleryImages.length > 1 && (
              <div className="thumbnail-gallery">
                {galleryImages.map((imgPath, index) => (
                  <img
                    key={index}
                    src={`https://automotive-backend-production.up.railway.app/${imgPath}`}
                    className={`thumbnail ${currentImageIndex === index ? "active" : ""}`}
                    alt={`${car.Name} thumbnail ${index + 1}`}
                    onClick={() => goToImage(index)}
                    onError={(e) => {
                      e.target.src = "/placeholder.svg?height=80&width=100";
                    }}
                  />
                ))}
              </div>
            )}

            <div className="features-section">
              <div className="features-header">
                <div className="features-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </div>
                <h5 className="features-title">Car Features</h5>
              </div>
              <div className="features-grid">
                {car.Feature?.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <div className="feature-check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </div>
                    <span className="feature-text">{feature.replace(/,\s*/g, ", ")}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Car Info */}
          <div className="col-lg-4">
            <div className="details-sidebar">
              <div style={{ marginBottom: "24px" }}>
                <h2 className="car-title">{car.Name}</h2>
                <div className="car-badges">
                  <span className="car-badge">{car.Year}</span>
                  <span className="car-badge">{car.Mileage?.toLocaleString()} Km</span>
                  <span className="car-badge">{car.Transmission}</span>
                  <span className="car-badge">{car.FuelType}</span>
                </div>
                <div className="price-container">
                  <div className="price-label">Price</div>
                  <div className="price-value">${car.Price?.toLocaleString()}</div>
                </div>
              </div>

              <div className="record-number text-muted d-flex justify-content-center align-items-center fw-bold">
                Record # : {car.RecordNumber}
              </div>

              <div className="specs-container">
                <h6 className="specs-title">Specifications</h6>
                <div className="specs-grid">
                  {specifications.map((spec, index) => (
                    <div key={index} className="spec-item">
                      <span className="spec-label">
                        <span>{spec.icon}</span> {spec.label}
                      </span>
                      <span className="spec-value">{spec.value || "N/A"}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="action-buttons">
                <button
                  className="message-button"
                  onClick={() => contactFormRef.current?.scrollIntoView({ behavior: "smooth" })}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="contact-form-section" ref={contactFormRef}>
          <ContactForm />
        </div>

        {relatedCars.length > 0 && (
          <div className="related-cars-section">
            <h5 className="related-cars-title">More Cars You Would Like!</h5>
            <div className="row g-3">
              {relatedCars.map((relatedCar) => {
                const relatedImage = relatedCar.Thumbnail
                  ? `https://automotive-backend-production.up.railway.app/${relatedCar.Thumbnail}`
                  : relatedCar.ImageURL?.[0]
                  ? `https://automotive-backend-production.up.railway.app/${relatedCar.ImageURL[0]}`
                  : "https://via.placeholder.com/300x200?text=No+Image";

                return (
                  <div key={relatedCar._id} className="col-md-3">
                    <Link to={`/card-details/${relatedCar.RecordNumber}`} className="card-link">
                      <Card
                        RecordNumber={relatedCar.RecordNumber}
                        title={relatedCar.Name}
                        image={relatedImage}
                        price={relatedCar.Price}
                        year={relatedCar.Year}
                        transmission={relatedCar.Transmission}
                        fuel={relatedCar.FuelType}
                        km={relatedCar.Mileage}
                      />
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
